import { ImageCategories } from 'config/enums'

/**
 * A 360 photo is equirectangular, which means its width is exactly twice its
 * height. Shape is what tells one apart from a regular photo, not the file
 * size: plenty of ordinary phone and camera photos are large too.
 */
const SPHERE_ASPECT_RATIO = 2
const SPHERE_ASPECT_RATIO_TOLERANCE = 0.02
const SPHERE_MIN_WIDTH = 2048

/**
 * Reads the pixel dimensions of a selected file without uploading it.
 * Resolves with null when the file cannot be read as an image.
 */
function readImageDimensions(file) {
  return new Promise((resolve) => {
    if (!file || typeof window.URL?.createObjectURL !== 'function') {
      resolve(null)
      return
    }

    const objectUrl = window.URL.createObjectURL(file)
    const image = new Image()

    const finish = (result) => {
      window.URL.revokeObjectURL(objectUrl)
      resolve(result)
    }

    image.onload = () =>
      finish({ width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = () => finish(null)
    image.src = objectUrl
  })
}

function isEquirectangular({ width, height }) {
  if (!width || !height) {
    return false
  }

  const ratio = width / height

  return (
    Math.abs(ratio - SPHERE_ASPECT_RATIO) <=
    SPHERE_ASPECT_RATIO * SPHERE_ASPECT_RATIO_TOLERANCE
  )
}

function isSpherePhoto(dimensions) {
  return isEquirectangular(dimensions) && dimensions.width >= SPHERE_MIN_WIDTH
}

/**
 * Checks a file against the rules of the category it is being uploaded to.
 * Resolves with null when the file is fine, or with the translation key and
 * values for the message to show.
 *
 * The two directions use the same test, so a photo that belongs in the 360
 * category is exactly the photo the regular categories turn away.
 */
export async function validateCarImageForCategory(file, category) {
  if (!file) {
    return null
  }

  const dimensions = await readImageDimensions(file)

  // Without dimensions there is nothing to judge, leave the decision to the
  // back-end rather than blocking an upload that might be fine.
  if (!dimensions) {
    return null
  }

  if (category !== ImageCategories.SPHERE_360) {
    return isSpherePhoto(dimensions)
      ? {
          key: 'carFileMediaTab.uploadValidation.sphereInRegularCategory',
          values: { name: file.name },
        }
      : null
  }

  if (!isEquirectangular(dimensions)) {
    return {
      key: 'carFileMediaTab.uploadValidation.notEquirectangular',
      values: {
        name: file.name,
        width: dimensions.width,
        height: dimensions.height,
      },
    }
  }

  if (dimensions.width < SPHERE_MIN_WIDTH) {
    return {
      key: 'carFileMediaTab.uploadValidation.sphereTooSmall',
      values: { name: file.name, minWidth: SPHERE_MIN_WIDTH },
    }
  }

  return null
}
